import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  ViewEncapsulation,
  forwardRef,
  QueryList,
} from '@angular/core';
import { FocusKeyManager } from '@angular/cdk/a11y';

import { PhxMenuItemComponent } from '../phx-menu-item/phx-menu-item.component';
import { ArrowLeft } from '../../icon/icon-map';

@Component({
  selector: 'phx-menu',
  templateUrl: './phx-menu.component.html',
  styleUrls: ['./phx-menu.component.scss'],
  exportAs: 'phxMenu',
  host: {
    class: 'phx-menu',
    '[class.phx-menu-multi-select]': 'multiple',
    '[class.phx-menu-single-select]': 'single',
    '[class.phx-menu-is-at-bottom]': 'isAtBottom',
    '[class.phx-menu-has-active-submenu]': 'activeSubmenu !== null',
    '[class.phx-menu-submenu-visible]': 'parentMenu !== null',
    '(keydown)': 'onKeydown($event)',
    '(focus)': 'onFocus()',
    '(scroll)': 'handleScroll()',
    role: 'menu',
    tabindex: '0',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxMenuComponent {
  @ContentChildren(forwardRef(() => PhxMenuItemComponent), { descendants: true })
  protected listItems?: QueryList<PhxMenuItemComponent>;
  protected keyManager: FocusKeyManager<PhxMenuItemComponent>;

  public isAtBottom: boolean = false;
  public activeSubmenu: PhxMenuComponent | null = null;
  public parentMenu: PhxMenuComponent | null = null;
  public heading: string | null = null;
  public ArrowLeft: string = ArrowLeft;

  constructor(private elRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.keyManager = new FocusKeyManager<PhxMenuItemComponent>(this.listItems)
      .withWrap()
      .skipPredicate(item => item.hasDivider);
    this.keyManager.setFirstItemActive();
    setTimeout(() => this.handleScroll());
  }

  public onKeydown(event: KeyboardEvent): void {
    this.keyManager.onKeydown(event);
  }

  public onFocus(): void {
    if (this.keyManager.activeItemIndex !== -1) {
      this.keyManager.setActiveItem(this.keyManager.activeItemIndex);
    } else {
      this.keyManager.setFirstItemActive();
    }
  }

  public setSubmenu(menu: PhxMenuComponent, heading?: string): void {
    // Clear any previously active submenu before activating the new one
    if (this.activeSubmenu && this.activeSubmenu !== menu) {
      this.activeSubmenu.parentMenu = null;
      this.activeSubmenu.heading = null;
    }
    this.activeSubmenu = menu;
    menu.parentMenu = this;
    menu.heading = heading ?? null;
    menu.cdr.markForCheck(); // trigger change detection to apply phx-menu-submenu-visible class
  }

  public goBack(event: MouseEvent): void {
    event.stopPropagation(); // prevent bubbling to phx-menu-item which would re-trigger setSubmenu()
    if (this.parentMenu) {
      this.parentMenu.activeSubmenu = null;
      this.parentMenu = null;
      this.heading = null;
      this.cdr.markForCheck(); // clear phx-menu-submenu-visible on this menu
    }
  }

  public handleScroll = (): void => {
    const { scrollTop, scrollHeight, clientHeight } = this.elRef.nativeElement;
    const buffer = 5;
    this.isAtBottom = scrollTop + clientHeight >= scrollHeight - buffer;
  };
}
